import { getClientEnv, isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  mapAiActionErrorBody,
  type AiActionErrorBody,
  AiActionError,
} from "@/features/ai/aiErrors";
import { mapAiChatCitations, mapAiGuardOutcome } from "@/features/ai/aiMappers";
import type {
  AiChatExchangeView,
  AiChatMessageInput,
  AiChatStreamResult,
} from "@/features/ai/ai.types";

type AiChatStreamChunk =
  | {
      delta: string;
      type: "chunk";
    }
  | {
      cacheStatus?: "hit" | "invalidated" | "miss" | "none" | null;
      citations?: unknown;
      creditsRemaining?: number | null;
      truncated?: boolean | null;
      type: "done";
    }
  | ({
      type: "error";
    } & AiActionErrorBody);

type StreamProjectAiChatInput = {
  activeChapterId: string | null;
  messages: AiChatMessageInput[];
  projectId: string;
  useLiveModel?: boolean;
};

type StreamProjectAiChatCallbacks = {
  onTextDelta?: (delta: string) => void;
};

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new AiActionError("access-denied", "PROJECT_ACCESS_DENIED");
  }

  return session.access_token;
}

function coerceStreamChunk(input: unknown): AiChatStreamChunk | null {
  if (!input || typeof input !== "object" || !("type" in input)) {
    return null;
  }

  const chunk = input as Record<string, unknown>;
  if (chunk.type === "chunk" && typeof chunk.delta === "string") {
    return {
      delta: chunk.delta,
      type: "chunk",
    };
  }

  if (chunk.type === "done") {
    return {
      cacheStatus:
        chunk.cacheStatus === "hit"
        || chunk.cacheStatus === "miss"
        || chunk.cacheStatus === "none"
        || chunk.cacheStatus === "invalidated"
          ? chunk.cacheStatus
          : null,
      citations: chunk.citations,
      creditsRemaining:
        typeof chunk.creditsRemaining === "number" ? chunk.creditsRemaining : null,
      truncated: chunk.truncated === true,
      type: "done",
    };
  }

  if (chunk.type === "error") {
    const cacheStatus = chunk.cacheStatus === "hit"
      ? "hit"
      : chunk.cacheStatus === "miss" || chunk.cacheStatus === "invalidated"
        ? "miss"
        : chunk.cacheStatus === "not-applicable" || chunk.cacheStatus === "none"
          ? "not-applicable"
          : null;

    return {
      cacheStatus,
      code: typeof chunk.code === "string" ? chunk.code : undefined,
      creditsSafe: chunk.creditsSafe === true,
      currentBalance:
        typeof chunk.currentBalance === "number" ? chunk.currentBalance : null,
      message: typeof chunk.message === "string" ? chunk.message : undefined,
      recommendedTier:
        chunk.recommendedTier === "author"
        || chunk.recommendedTier === "crafter"
        || chunk.recommendedTier === "free"
          ? chunk.recommendedTier
          : null,
      resetAt:
        typeof chunk.resetAt === "string" && chunk.resetAt.trim().length > 0
          ? chunk.resetAt
          : null,
      retryAfterSeconds:
        typeof chunk.retryAfterSeconds === "number" ? chunk.retryAfterSeconds : null,
      stage: typeof chunk.stage === "string" ? chunk.stage : null,
      type: "error",
      truncated: chunk.truncated === true,
      usesRemainingToday:
        typeof chunk.usesRemainingToday === "number" ? chunk.usesRemainingToday : null,
    };
  }

  return null;
}

async function readErrorResponse(response: Response): Promise<never> {
  const body = await response.json().catch(() => null);
  throw mapAiActionErrorBody(
    body,
    typeof body === "object" && body && "code" in body
      ? String(body.code)
      : `AI_CHAT_FAILED_${response.status}`,
  );
}

export function buildProjectAiChatMessages(
  exchanges: AiChatExchangeView[],
  question: string,
): AiChatMessageInput[] {
  return [
    ...exchanges.flatMap<AiChatMessageInput>((exchange) => ([
      {
        content: exchange.question,
        role: "user" as const,
      },
      {
        content: exchange.answer,
        role: "assistant" as const,
      },
    ])),
    {
      content: question,
      role: "user" as const,
    },
  ];
}

export async function streamProjectAiChat(
  input: StreamProjectAiChatInput,
  callbacks: StreamProjectAiChatCallbacks = {},
): Promise<AiChatStreamResult> {
  if (!isSupabaseConfigured()) {
    throw new AiActionError("failed", "AI_CHAT_FAILED");
  }

  const response = await fetch(`${getClientEnv().supabaseUrl}/functions/v1/ai-chat`, {
    body: JSON.stringify({
      ...input,
      useLiveModel: input.useLiveModel ?? true,
    }),
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  if (!response.ok) {
    await readErrorResponse(response);
  }

  if (!response.body) {
    throw new AiActionError("failed", "AI_CHAT_FAILED");
  }

  const decoder = new TextDecoder();
  const reader = response.body.getReader();
  let answer = "";
  let citations: AiChatStreamResult["citations"] = [];
  let creditsRemaining: number | null = null;
  let guardOutcome = mapAiGuardOutcome({
    cacheStatus: null,
    feature: "chat",
    truncated: false,
  });
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }

      const chunk = coerceStreamChunk(JSON.parse(trimmed));
      if (!chunk) {
        continue;
      }

      if (chunk.type === "chunk") {
        answer += chunk.delta;
        callbacks.onTextDelta?.(chunk.delta);
        continue;
      }

      if (chunk.type === "error") {
        throw mapAiActionErrorBody(chunk, chunk.message ?? "AI_CHAT_FAILED");
      }

      citations = mapAiChatCitations(input.projectId, chunk.citations);
      creditsRemaining = chunk.creditsRemaining ?? null;
      guardOutcome = mapAiGuardOutcome({
        cacheStatus: chunk.cacheStatus,
        feature: "chat",
        truncated: chunk.truncated === true,
      });
    }
  }

  if (buffer.trim().length > 0) {
    const chunk = coerceStreamChunk(JSON.parse(buffer));

    if (chunk?.type === "error") {
      throw mapAiActionErrorBody(chunk, chunk.message ?? "AI_CHAT_FAILED");
    }

    if (chunk?.type === "done") {
      citations = mapAiChatCitations(input.projectId, chunk.citations);
      creditsRemaining = chunk.creditsRemaining ?? null;
      guardOutcome = mapAiGuardOutcome({
        cacheStatus: chunk.cacheStatus,
        feature: "chat",
        truncated: chunk.truncated === true,
      });
    }
  }

  return {
    answer: answer.trim(),
    citations,
    creditsRemaining,
    guardOutcome,
  };
}
